import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

import {
  infoBadgeColors,
  invertedSnackbarSurfaceColor,
  onInvertedSnackbarSurfaceColor
} from "@/src/theme/helpers";
import { radius, spacing } from "@/src/theme/tokens";

/** Layout-only gap between the hide/info/favourite icon boxes. */
export const auctionOverlayIconsContainerSx = (
  theme: Theme
): SystemStyleObject<Theme> => ({
  gap: theme.spacing(spacing.xs)
});

export const auctionOverlayIconBoxSx = (
  theme: Theme
): SystemStyleObject<Theme> => ({
  display: "flex",
  width: "34px",
  height: "34px",
  padding: 0,
  justifyContent: "center",
  alignItems: "center",
  aspectRatio: "1/1",
  filter: `drop-shadow(0 0 1.5px ${alpha(theme.palette.common.black, 0.45)})`
});

export const getAuctionItemQuickAuctionChipSx =
  (): SxProps<Theme> => (theme) => {
    const { backgroundColor, color } = infoBadgeColors(theme);

    return {
      backgroundColor,
      color,
      border: `1px solid ${color}`,
      py: spacing.xs / 2,
      px: 0.75,
      borderRadius: radius.xs,
      fontFamily: theme.typography.subtitle3.fontFamily,
      fontSize: theme.typography.subtitle3.fontSize,
      fontWeight: theme.typography.fontWeightSemibold,
      lineHeight: theme.typography.subtitle3.lineHeight,
      letterSpacing: "0.07px",
      zIndex: 4,
      boxShadow: `0 0 7px ${alpha(theme.palette.common.black, 0.25)}`,
      m: spacing.xs / 2,
      display: "flex",
      alignItems: "center",
      gap: "2px",
      bottom: theme.spacing(spacing.xs)
    };
  };

export const auctionItemFinalOfferChipSx: SxProps<Theme> = (theme) => ({
  position: "absolute",
  top: theme.spacing(spacing.sm),
  left: theme.spacing(spacing.sm),
  backgroundColor: alpha(theme.palette.common.black, 0.7),
  py: spacing.xs,
  px: spacing.sm,
  borderRadius: radius.xs,
  zIndex: 4,
  boxShadow: `2px 2px 4px ${theme.palette.misc.outlineBorder}`,
  display: "flex",
  alignItems: "center",
  gap: spacing.xs
});

export const auctionItemFinalOfferSubmittedChipSx: SxProps<Theme> = (
  theme
) => ({
  position: "absolute",
  top: theme.spacing(spacing.sm),
  left: theme.spacing(spacing.sm),
  backgroundColor: theme.palette.semantic.success.main,
  py: spacing.xs,
  px: spacing.sm,
  borderRadius: radius.xs,
  zIndex: 4,
  boxShadow: `2px 2px 4px ${theme.palette.misc.outlineBorder}`,
  display: "flex",
  alignItems: "center",
  gap: spacing.xs
});

/**
 * Target price chip pinned to the auction card photo. The Snackbar surface it
 * used inverts per mode, so the two fills are swapped here: the dark fill on
 * the dark card and the light fill on the light one.
 */
export const getAuctionItemTargetPriceChipSx =
  (top: string): SxProps<Theme> =>
  (theme) => ({
    position: "absolute",
    top,
    left: 0,
    backgroundColor: invertedSnackbarSurfaceColor(theme),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    py: spacing.xs,
    px: "9px",
    borderRadius: `0 0 ${radius.sm} 0`,
    zIndex: 4,
    boxShadow: `2px 2px 4px ${theme.palette.misc.outlineBorder}`,
    color: onInvertedSnackbarSurfaceColor(theme)
  });
